import { useEffect, useRef, useCallback, useMemo } from "react";
import * as THREE from 'three';
import { Line2 } from "three/examples/jsm/Addons.js";
import { LineGeometry } from "three/examples/jsm/Addons.js";
import { LineMaterial } from "three/examples/jsm/Addons.js";
import * as GeomtryUtils from 'three/addons/utils/GeometryUtils.js'
// import { createBoundaryEdgesHelper } from "../../../utils/holeFilling";

// --- OPTIMIZATION: Create a cache outside the component ---
// This map will store computed boundaries, keyed by the geometry's UUID.
// It persists across re-renders and component unmounts.
const boundaryCache = new Map();

/**
 * An optimized, high-performance hook for managing mesh highlighting.
 * It pre-computes all boundary edges once PER GEOMETRY and caches the result,
 * preventing re-computation and solving performance issues.
 * @param {THREE.Mesh} mesh The mesh to highlight.
 * @param {THREE.Scene} scene The scene to add helpers to.
 * @returns {function} A function to update the highlights based on a set of selected vertices.
 */
export function useFillHighlight(mesh, scene) {
    const originalColorsRef = useRef(null);
    const boundaryHighlightRef = useRef(null);
    const allBoundaryEdgesRef = useRef([]);

    const REGION_COLOR = new THREE.Color('rgb(245,243,0)');
    const BOUNDARY_COLOR = new THREE.Color('rgb(109,45,181)');

    // Effect to initialize resources and pre-compute boundaries
    useEffect(() => {
        if (!mesh || !scene) return;

        // --- FIX: Check if the highlighter is already initialized for this geometry ---
        // This prevents re-creating objects on every re-render, solving the lag.
        if (boundaryHighlightRef.current && boundaryHighlightRef.current.userData.geomUUID === mesh.geometry.uuid) {
            return; // Already initialized, do nothing.
        }

        // --- Cleanup previous highlighter if it exists from a different mesh ---
        if (boundaryHighlightRef.current) {
            scene.remove(boundaryHighlightRef.current);
            boundaryHighlightRef.current.geometry.dispose();
            boundaryHighlightRef.current.material.dispose();
        }

        // --- Initialize Vertex Color Cache ---
        if (mesh.geometry.attributes.color) {
            const colArray = new Float32Array(mesh.geometry.attributes.color.array);
            originalColorsRef.current = colArray;
            mesh.geometry.userData.originalColors = colArray;
        }

        // --- OPTIMIZATION: Use the cache to get boundary edges ---
        const geomUUID = mesh.geometry.uuid;
        if (boundaryCache.has(geomUUID)) {
            // Use the cached result instantly
            allBoundaryEdgesRef.current = boundaryCache.get(geomUUID);
        } else {
            // Compute once and store in the cache for next time
            const newBoundaryEdges = computeAllBoundaryEdges(mesh.geometry);
            allBoundaryEdgesRef.current = newBoundaryEdges;
            boundaryCache.set(geomUUID, newBoundaryEdges);
        }
        
        // --- Initialize Boundary Highlight Object (Create Once) ---
        const lineGeometry = new THREE.BufferGeometry();
        const linePositions = new Float32Array(allBoundaryEdgesRef.current.length * 2 * 3);
        
        const positionAttribute = new THREE.Float32BufferAttribute(linePositions, 3);
        positionAttribute.setUsage(THREE.DynamicDrawUsage);
        lineGeometry.setAttribute('position', positionAttribute);
        lineGeometry.setDrawRange(0, 0);

        const lineMaterial = new THREE.LineBasicMaterial({
            color: BOUNDARY_COLOR, // Red for the boundary
            linewidth: 3,
            depthTest: false,
            transparent: true,
        });

        const lineSegments = new THREE.LineSegments(lineGeometry, lineMaterial);
        lineSegments.renderOrder = 999;
        
        scene.add(lineSegments);
        boundaryHighlightRef.current = lineSegments;

        // --- Cleanup Function ---
        return () => {
            if (boundaryHighlightRef.current) {
                scene.remove(boundaryHighlightRef.current);
                boundaryHighlightRef.current.geometry.dispose();
                boundaryHighlightRef.current.material.dispose();
                boundaryHighlightRef.current = null;
            }
            if (originalColorsRef.current && mesh.geometry.attributes.color) {
                mesh.geometry.attributes.color.array.set(originalColorsRef.current);
                mesh.geometry.attributes.color.needsUpdate = true;
            }
        };
    }, [mesh, scene]);

    // This is the function we will call on every paint update
    const updateHighlights = useCallback((selectedVertices) => {
        if (!mesh || !boundaryHighlightRef.current) return;

        const geom = mesh.geometry;
        const colors = geom.attributes.color;
        const positions = geom.attributes.position;

        // --- 1. Update Vertex Colors ---
        if (originalColorsRef.current && colors) {
            colors.array.set(originalColorsRef.current);
            selectedVertices.forEach(index => {
                colors.setXYZ(index, REGION_COLOR.r, REGION_COLOR.g, REGION_COLOR.b);
            });
            colors.needsUpdate = true;
        }
        
        // --- 2. Update Boundary Highlight Data ---
        const boundaryLineVertices = [];
        allBoundaryEdgesRef.current.forEach(edge => {
            if (selectedVertices.has(edge.v1) || selectedVertices.has(edge.v2)) {
                boundaryLineVertices.push(positions.getX(edge.v1), positions.getY(edge.v1), positions.getZ(edge.v1));
                boundaryLineVertices.push(positions.getX(edge.v2), positions.getY(edge.v2), positions.getZ(edge.v2));
            }
        });
        
        const positionAttribute = boundaryHighlightRef.current.geometry.attributes.position;
        positionAttribute.array.set(boundaryLineVertices);
        positionAttribute.needsUpdate = true;
        boundaryHighlightRef.current.geometry.setDrawRange(0, boundaryLineVertices.length / 3);
    }, [mesh]);

    return updateHighlights;
}

/**
 * A helper function that computes and returns all boundary edges.
 * @param {THREE.BufferGeometry} geometry
 * @returns {Array<{v1: number, v2: number}>}
 */
function computeAllBoundaryEdges(geometry) {
    if (!geometry.index || !geometry.attributes.position) {
        return [];
    }
  
    const edgeMap = new Map();
    const indices = geometry.index.array;
  
    for (let i = 0; i < indices.length; i += 3) {
      for (let j = 0; j < 3; j++) {
        const i1 = indices[i + j];
        const i2 = indices[i + ((j + 1) % 3)];
        const key = `${Math.min(i1, i2)}-${Math.max(i1, i2)}`;
        edgeMap.set(key, (edgeMap.get(key) || 0) + 1);
      }
    }
  
    const boundaryEdges = [];
    for (const [key, count] of edgeMap.entries()) {
      if (count === 1) {
        const [v1, v2] = key.split('-').map(Number);
        boundaryEdges.push({ v1, v2 });
      }
    }
    return boundaryEdges;
}